import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available themes: light, dark, midnight, classic
const THEMES = ['light', 'dark', 'midnight', 'classic'];

// Theme display names
const THEME_NAMES = {
  light: 'Light',
  dark: 'Dark',
  midnight: 'Midnight',
  classic: 'Classic'
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored && THEMES.includes(stored)) return stored;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    // Remove all theme classes
    root.classList.remove('light', 'dark', 'midnight', 'classic');
    // Migrate legacy themes
    if (theme === 'sunrise' || theme === 'tropical') {
      setTheme('classic');
      return;
    }
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };
  
  // Cycle through all themes
  const cycleTheme = () => {
    setTheme(prev => {
      const currentIndex = THEMES.indexOf(prev);
      const nextIndex = (currentIndex + 1) % THEMES.length;
      return THEMES[nextIndex];
    });
  };
  
  // Get display name for a theme
  const getThemeName = (t) => THEME_NAMES[t] || t;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, cycleTheme, themes: THEMES, getThemeName }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
